import { idsMatch, isSameChatMessage } from "./conversationUtils";
import { loadedImagesCache } from "./imageLoadCache";

/** Survives Conversation unmount / chat switch so in-flight sends stay visible. */
const pendingByChat = new Map();

function normalizeKeys(chatId, extraKeys = []) {
  const keys = [chatId, ...extraKeys].filter((k) => k != null && k !== "");
  return [...new Set(keys.map(String))];
}

function getMapForKeys(keys) {
  for (const k of keys) {
    if (pendingByChat.has(k)) return pendingByChat.get(k);
  }
  return null;
}

export function mediaUrl(msg) {
  if (!msg) return "";
  const url = Array.isArray(msg.fileUrl) ? msg.fileUrl[0] : msg.fileUrl;
  return url ? String(url) : "";
}

export function isBlobOrDataUrl(url) {
  if (!url || typeof url !== "string") return false;
  return url.startsWith("blob:") || url.startsWith("data:");
}

export function isHttpUrl(url) {
  if (!url || typeof url !== "string") return false;
  return url.startsWith("http://") || url.startsWith("https://");
}

export function hasPendingOutgoing(chatId, extraKeys = []) {
  const map = getMapForKeys(normalizeKeys(chatId, extraKeys));
  return !!(map && map.size);
}

export function addPendingOutgoing(chatId, messages, extraKeys = []) {
  const keys = normalizeKeys(chatId, extraKeys);
  if (!keys.length || !messages?.length) return;
  let map = getMapForKeys(keys);
  if (!map) map = new Map();
  for (const k of keys) pendingByChat.set(k, map);
  for (const msg of messages) {
    const id = String(msg.messageRequestId || msg.id || "");
    if (!id) continue;
    map.set(id, { ...msg, local: true, uploading: msg.uploading !== false });
  }
}

export function removePendingOutgoing(chatId, messageRequestId, extraKeys = []) {
  const keys = normalizeKeys(chatId, extraKeys);
  const map = getMapForKeys(keys);
  if (!map || messageRequestId == null || messageRequestId === "") return;
  map.delete(String(messageRequestId));
  if (map.size === 0) {
    for (const [k, m] of [...pendingByChat.entries()]) {
      if (m === map) pendingByChat.delete(k);
    }
  }
}

export function getPendingOutgoing(chatId, extraKeys = []) {
  const map = getMapForKeys(normalizeKeys(chatId, extraKeys));
  return map ? Array.from(map.values()) : [];
}

export function lookupPendingBlob(msg, chatId, extraKeys = []) {
  if (!msg) return "";
  const pending = getPendingOutgoing(chatId, extraKeys);
  const match = pending.find((p) => isSameChatMessage(p, msg) || isSameChatMessage(msg, p));
  if (!match) return "";
  const url = mediaUrl(match);
  return isBlobOrDataUrl(url) ? url : "";
}

export function registerOutgoingMessages(chatId, messages, formatDateFn, extraKeys = []) {
  const sectionDate = formatDateFn ? formatDateFn(new Date()) : "";
  addPendingOutgoing(
    chatId,
    (messages || []).map((m) => ({
      ...m,
      _sectionDate: m._sectionDate || sectionDate,
      uploading: true,
    })),
    extraKeys
  );
}

export function findChatHistoryIndex(history, chatId, extraKeys = []) {
  const keys = normalizeKeys(chatId, extraKeys);
  return (history || []).findIndex((item) => keys.some((k) => idsMatch(item?.id, k)));
}

export function upsertChatHistory(prev, chatId, data, extraKeys = []) {
  const list = prev || [];
  const idx = findChatHistoryIndex(list, chatId, extraKeys);
  if (idx > -1) {
    return list.map((item, i) => (i === idx ? { ...item, data } : item));
  }
  return [...list, { id: chatId, data }];
}

function overlayPendingOnServer(serverMsg, pendingMsg) {
  const blobUrl = mediaUrl(pendingMsg);
  if (!isBlobOrDataUrl(blobUrl)) return serverMsg;
  const serverUrl = mediaUrl(serverMsg);
  const serverFileUrl = isHttpUrl(serverUrl) ? serverMsg.fileUrl : serverMsg.serverFileUrl;
  const pendingImages = Array.isArray(pendingMsg.images) && pendingMsg.images.length
    ? pendingMsg.images
    : null;
  return {
    ...serverMsg,
    fileUrl: pendingMsg.fileUrl,
    images: pendingImages
      ? pendingImages.map((img, i) => ({
          ...(serverMsg.images && serverMsg.images[i] ? serverMsg.images[i] : {}),
          ...img,
          fileUrl: img.fileUrl || pendingMsg.fileUrl,
          local: true,
          serverFileUrl,
        }))
      : serverMsg.images,
    local: true,
    uploading: true,
    serverFileUrl,
    messageRequestId: pendingMsg.messageRequestId || serverMsg.messageRequestId,
    mediaAlbumId: pendingMsg.mediaAlbumId || serverMsg.mediaAlbumId,
    attachedFileName: pendingMsg.attachedFileName || serverMsg.attachedFileName,
    createdAtTs: pendingMsg.createdAtTs || serverMsg.createdAtTs,
    width: pendingMsg.width || serverMsg.width,
    height: pendingMsg.height || serverMsg.height,
  };
}

export function injectPendingOutgoing(chatId, sections, formatDateFn, extraKeys = []) {
  const pending = getPendingOutgoing(chatId, extraKeys);
  if (!pending.length) return sections || [];

  const result = (sections || []).map((s) => ({
    ...s,
    items: [...(s.items || [])],
  }));

  for (const msg of pending) {
    let matched = null;
    let matchedSection = null;
    let matchedIndex = -1;
    for (const section of result) {
      const idx = section.items.findIndex((m) => isSameChatMessage(msg, m) || isSameChatMessage(m, msg));
      if (idx !== -1) {
        matched = section.items[idx];
        matchedSection = section;
        matchedIndex = idx;
        break;
      }
    }

    if (matched) {
      const serverUrl = mediaUrl(matched.serverFileUrl) || mediaUrl(matched);
      if (isHttpUrl(serverUrl) && loadedImagesCache.has(serverUrl)) {
        removePendingOutgoing(chatId, msg.messageRequestId || msg.id, extraKeys);
        continue;
      }
      matchedSection.items[matchedIndex] = overlayPendingOnServer(matched, msg);
      continue;
    }

    const dateStr = msg._sectionDate || (formatDateFn ? formatDateFn(msg.createdAt) : "");
    let section = result.find((s) => s.createdAt === dateStr);
    if (!section) {
      section = { createdAt: dateStr, items: [] };
      result.push(section);
    }
    section.items.push(msg);
  }
  return result;
}

export function applyPendingOutgoingToHistory(prev, chatId, formatDateFn, extraKeys = []) {
  if (!hasPendingOutgoing(chatId, extraKeys)) return prev;
  const idx = findChatHistoryIndex(prev, chatId, extraKeys);
  const existing = idx > -1 ? prev[idx].data : [];
  const data = injectPendingOutgoing(chatId, existing, formatDateFn, extraKeys);
  const storeId = idx > -1 ? prev[idx].id : chatId;
  return upsertChatHistory(prev || [], storeId, data, extraKeys);
}
